import { type BaseRendererComponentProps, BaseModel, BaseModelProps } from '@mail/3promo';
import { ReactRenderer } from '@mail/3promo/render/react';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import React from 'react';
import { IS_MOBILE_OR_TABLET_BROWSER, IS_WEBVIEW, IS_WEBVIEW_FROM_UA } from 'reactApp/appHelpers/configHelpers';
import { config, PROMO_ID } from 'reactApp/appHelpers/featuresHelpers/features/familyUpgrade2';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UpgradeSubscriptionSelectors } from 'reactApp/modules/upgradeSubscription/upgradeSubscription.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { store as reduxStore } from 'reactApp/store';
import { sendXray } from 'reactApp/utils/ga';

class PopupModel extends BaseModel<BaseModelProps> {
    getParent(): HTMLElement | null {
        return document.documentElement;
    }
}

const LazyComponent = React.lazy(() => import('reactApp/ui/FamilyUpgradeModal2/FamilyUpgradeModal2'));

const SuspendedComponent = <P extends BaseRendererComponentProps>(props: P) => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent {...props} />
        </React.Suspense>
    );
};

export const familyUpgradeModalCreator2: ModelCreator = ({ models, render, stores }) => {
    return new PopupModel(
        PROMO_ID,
        models.base,
        new BaseModelProps(PROMO_ID, {
            store: new LocalStorageStore(PROMO_ID, stores.localStorage),
            renderer: new ReactRenderer(PROMO_ID, SuspendedComponent, render.react),
            closable: true,
            showLimit: config.showLimit,
            closeLimit: config.closeLimit,
            showInterval: config.showInterval,
            templateData: {},
            ignoreTestOnRedraw: true,
            markAsCompleted: true,
            hideOnCompleted: true,
            hideOnSnooze: true,
            test: (resolve, reject) => {
                const state = reduxStore.getState();
                const storage = getCurrentStorage(state);

                if (IS_MOBILE_OR_TABLET_BROWSER || IS_WEBVIEW || IS_WEBVIEW_FROM_UA) {
                    reject();
                    return;
                }

                if (storage !== EStorageType.home) {
                    reject();
                    return;
                }

                const { action = '' } = SettingsSelectors.getQueryParams(state);
                if (action) {
                    reject();
                    sendXray(['promo-test', PROMO_ID, 'rejectbyaction']);
                    return;
                }

                const isBizUser = UserSelectors.isBizUser(state);
                const isCorpUser = UserSelectors.isCorpUser(state);
                const isPaidUser = UserSelectors.isPaidUser(state);

                const isUpgradeAvaliable = UpgradeSubscriptionSelectors.isUpgradeAvaliable(state);

                const isReject = isBizUser || !isPaidUser || isCorpUser || !isUpgradeAvaliable;

                sendXray(['promo-test', PROMO_ID, isReject ? 'reject' : 'resolve']);

                if (isReject) {
                    reject();
                    return;
                }

                resolve();
            },
        })
    );
};
